import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { MeetingProvider, MeetingConsumer, useMeeting, useParticipant } from '@videosdk.live/react-sdk';
import ReactPlayer from 'react-player';
import '../styles/chatPage.css';
// Analytics imports
import { trackEvent } from '../firebase/analytics';
import { ANALYTICS_EVENTS } from '../firebase/analyticsEvents';

// ParticipantView component following the exact VideoSDKTest.js pattern
function ParticipantView({ participantId, isRemote = false }) {
  const micRef = useRef(null);
  const videoRef = useRef(null);
  const { webcamStream, micStream, webcamOn, micOn, isLocal, displayName } = useParticipant(participantId);

  // Create video stream using the exact VideoSDKTest.js pattern
  const videoStream = useMemo(() => {
    if (webcamOn && webcamStream) {
      const mediaStream = new MediaStream();
      mediaStream.addTrack(webcamStream.track);
      console.log(`[DEBUG] Created video stream for ${isRemote ? 'remote' : 'local'} participant:`, mediaStream);
      return mediaStream;
    }
    return null;
  }, [webcamStream, webcamOn, isRemote]);

  // Set video stream to video element
  useEffect(() => {
    if (videoRef.current && videoStream) {
      console.log(`[DEBUG] Setting video srcObject for ${isRemote ? 'remote' : 'local'} participant`);
      videoRef.current.srcObject = videoStream;
      
      // Handle play promise properly to avoid interruption errors
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          // Only log actual errors, not interruptions
          if (error.name !== 'AbortError') {
            console.error(`[DEBUG] Video play failed for ${isRemote ? 'remote' : 'local'}:`, error);
          }
        });
      }
    } else if (videoRef.current && !videoStream) {
      // Safely clear the video source
      if (videoRef.current.srcObject) {
        videoRef.current.pause();
        videoRef.current.srcObject = null;
      }
    }
  }, [videoStream, isRemote]);

  // Handle audio stream using the exact VideoSDKTest.js pattern
  useEffect(() => {
    if (micRef.current) {
      if (micOn && micStream) {
        const mediaStream = new MediaStream();
        mediaStream.addTrack(micStream.track);

        micRef.current.srcObject = mediaStream;
        micRef.current
          .play()
          .catch((error) =>
            console.error("Audio play failed", error)
          );
      } else {
        micRef.current.srcObject = null;
      }
    }
  }, [micStream, micOn]);

  const containerStyle = {
    position: 'relative',
    margin: '10px',
    padding: '10px',
    border: isRemote ? '2px solid #4CAF50' : '2px solid #2196F3',
    borderRadius: '8px',
    backgroundColor: 'rgba(0,0,0,0.1)',
    minWidth: '320px'
  };

  const labelStyle = {
    position: 'absolute',
    top: '-12px',
    left: '10px',
    backgroundColor: isRemote ? '#4CAF50' : '#2196F3',
    color: 'white',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 'bold'
  };

  return (
    <div style={containerStyle}>
      <div style={labelStyle}>
        {isRemote ? 'REMOTE' : 'LOCAL'} - {displayName || participantId}
      </div>
      
      <div style={{ marginTop: '10px' }}>
        <p style={{ margin: '5px 0', fontSize: '14px' }}>
          Webcam: <span style={{ color: webcamOn ? '#4CAF50' : '#f44336' }}>
            {webcamOn ? "ON" : "OFF"}
          </span> | 
          Mic: <span style={{ color: micOn ? '#4CAF50' : '#f44336' }}>
            {micOn ? "ON" : "OFF"}
          </span>
        </p>
        
        <audio ref={micRef} autoPlay muted={isLocal} />
        
        {webcamOn && videoStream ? (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{
              width: '300px',
              height: '200px',
              borderRadius: '4px',
              backgroundColor: '#000'
            }}
            onLoadedMetadata={() => {
              console.log(`${isRemote ? 'Remote' : 'Local'} video loaded`);
            }}
            onError={(err) => {
              console.log(`${isRemote ? 'Remote' : 'Local'} video error:`, err);
            }}
          />
        ) : (
          <div style={{
            width: '300px',
            height: '200px',
            backgroundColor: '#333',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#666',
            borderRadius: '4px'
          }}>
            {webcamOn ? 'Loading video...' : 'Camera Off'}
          </div>
        )}
      </div>
    </div>
  );
}

// Controls component following VideoSDKTest.js pattern
function Controls() {
  const { leave, toggleMic, toggleWebcam, localMicOn, localWebcamOn } = useMeeting();
  
  const buttonStyle = {
    margin: '5px',
    padding: '8px 16px',
    borderRadius: '4px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 'bold'
  };

  return (
    <div style={{ 
      display: 'flex', 
      gap: '10px', 
      margin: '20px 0',
      flexWrap: 'wrap',
      justifyContent: 'center'
    }}>
      <button 
        onClick={() => toggleWebcam()} 
        style={{
          ...buttonStyle,
          backgroundColor: localWebcamOn ? '#f44336' : '#4CAF50',
          color: 'white'
        }}
      >
        {localWebcamOn ? 'Turn Off Camera' : 'Turn On Camera'}
      </button>
      <button 
        onClick={() => toggleMic()}
        style={{
          ...buttonStyle,
          backgroundColor: localMicOn ? '#f44336' : '#4CAF50',
          color: 'white'
        }}
      >
        {localMicOn ? 'Mute Mic' : 'Unmute Mic'}
      </button>
      <button 
        onClick={() => leave()}
        style={{
          ...buttonStyle,
          backgroundColor: '#FF9800',
          color: 'white'
        }}
      >
        Leave Meeting
      </button>
    </div>
  );
}

// Main meeting view following VideoSDKTest.js pattern
function MeetingView({ meetingId, onMeetingLeave }) {
  const [joined, setJoined] = useState(null);
  const { join, participants } = useMeeting({
    onMeetingJoined: () => {
      console.log("Meeting joined successfully");
      setJoined("JOINED");
    },
    onMeetingLeft: () => {
      console.log("Meeting left");
      onMeetingLeave();
    },
    onParticipantJoined: (participant) => {
      console.log("Participant joined:", participant);
    },
    onParticipantLeft: (participant) => {
      console.log("Participant left:", participant);
    }
  });

  const joinMeeting = useCallback(() => {
    setJoined("JOINING");
    join();
  }, [join]);

  // Separate local and remote participants
  const participantIds = [...participants.keys()];
  const localParticipants = participantIds.filter(id => {
    const participant = participants.get(id);
    return participant?.isLocal;
  });
  const remoteParticipants = participantIds.filter(id => {
    const participant = participants.get(id);
    return !participant?.isLocal;
  });

  const containerStyle = {
    padding: '20px',
    maxWidth: '1200px',
    margin: '0 auto',
    backgroundColor: '#f5f5f5',
    minHeight: '100vh'
  };

  const headerStyle = {
    textAlign: 'center',
    marginBottom: '20px',
    padding: '15px',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  };

  if (joined === "JOINED") {
    return (
      <div style={containerStyle}>
        <div style={headerStyle}>
          <h2 style={{ margin: 0, color: '#333' }}>Camera Test - Meeting: {meetingId}</h2>
          <p style={{ margin: '10px 0 0 0', color: '#666' }}>
            Participants: {participantIds.length} | 
            Local: {localParticipants.length} | 
            Remote: {remoteParticipants.length}
          </p>
        </div>

        <Controls />

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: '20px',
          marginTop: '20px'
        }}>
          {/* Local participants */}
          {localParticipants.map((participantId) => (
            <ParticipantView
              participantId={participantId}
              key={participantId}
              isRemote={false}
            />
          ))}

          {/* Remote participants */}
          {remoteParticipants.map((participantId) => (
            <ParticipantView
              participantId={participantId}
              key={participantId}
              isRemote={true}
            />
          ))}
        </div>

        {remoteParticipants.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            backgroundColor: 'white',
            borderRadius: '8px',
            margin: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ color: '#666' }}>Waiting for other participants...</h3>
            <p style={{ color: '#999' }}>
              Share this meeting ID with others: <strong>{meetingId}</strong>
            </p>
          </div>
        )}
      </div>
    );
  }

  if (joined === "JOINING") {
    return (
      <div style={{...containerStyle, textAlign: 'center'}}>
        <div style={headerStyle}>
          <h2>Joining Meeting...</h2>
          <p>Meeting ID: {meetingId}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{...containerStyle, textAlign: 'center'}}>
      <div style={headerStyle}>
        <h2>Camera Test</h2>
        <p>Meeting ID: {meetingId}</p>
        <button 
          onClick={joinMeeting}
          style={{
            padding: '12px 24px',
            fontSize: '16px',
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            marginTop: '20px'
          }}
        >
          Join Meeting & Start Camera Test
        </button>
      </div>
    </div>
  );
}

// Join screen component
function JoinScreen({ getMeetingAndToken }) {
  const [meetingId, setMeetingId] = useState("");

  const handleJoinOrCreate = async (createNew = false) => {
    await getMeetingAndToken(createNew ? null : meetingId);
  };

  const containerStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    padding: '20px'
  };

  const cardStyle = {
    backgroundColor: 'white',
    padding: '40px',
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
    maxWidth: '400px',
    width: '100%'
  };

  const inputStyle = {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    border: '2px solid #ddd',
    borderRadius: '6px',
    marginBottom: '20px',
    boxSizing: 'border-box'
  };

  const buttonStyle = {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    margin: '5px 0',
    fontWeight: 'bold'
  };

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <h1 style={{ textAlign: 'center', marginBottom: '30px', color: '#333' }}>
          VideoSDK Camera Test
        </h1>
        
        <input
          type="text"
          placeholder="Enter Meeting ID (optional)"
          value={meetingId}
          onChange={(e) => setMeetingId(e.target.value)}
          style={inputStyle}
        />
        
        <button 
          onClick={() => handleJoinOrCreate(false)}
          disabled={!meetingId.trim()}
          style={{
            ...buttonStyle,
            backgroundColor: meetingId.trim() ? '#2196F3' : '#ccc',
            color: 'white'
          }}
        >
          Join Existing Meeting
        </button>
        
        <div style={{ textAlign: 'center', margin: '15px 0', color: '#666' }}>
          or
        </div>
        
        <button 
          onClick={() => handleJoinOrCreate(true)}
          style={{
            ...buttonStyle,
            backgroundColor: '#4CAF50',
            color: 'white'
          }}
        >
          Create New Meeting
        </button>
      </div>
    </div>
  );
}

// Main CameraTestPage component
const CameraTestPage = ({ onBack }) => {
  const [meetingId, setMeetingId] = useState(null);

  // VideoSDK auth token (from VideoSDKTest.js)
  const authToken = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJhcGlrZXkiOiI5NWZhMzRmOS1hMDU2LTQxYzctOTEwOS1hN2RiNGM2Yjk3YTYiLCJwZXJtaXNzaW9ucyI6WyJhbGxvd19qb2luIl0sImlhdCI6MTc1MDkyNzg2NSwiZXhwIjoxNzUxNTMyNjY1fQ.FNn6F0GutUFQi44t3PtCYWvE7dDjpi2lZlYbeXzuAf4";

  // Create meeting function (from VideoSDKTest.js)
  const createMeeting = async () => {
    const res = await fetch(`https://api.videosdk.live/v2/rooms`, {
      method: "POST",
      headers: {
        authorization: `${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({}),
    });

    const { roomId } = await res.json();
    return roomId;
  };

  const getMeetingAndToken = useCallback(async (id) => {
    try {
      const newMeetingId = id == null ? await createMeeting() : id;
      console.log("Setting meeting ID:", newMeetingId);
      setMeetingId(newMeetingId);
    } catch (error) {
      console.error("Error getting meeting:", error);
      alert("Failed to create/join meeting. Please try again.");
    }
  }, []);

  const onMeetingLeave = useCallback(() => {
    console.log("Meeting left, returning to join screen");
    setMeetingId(null);
    if (onBack) {
      onBack();
    }
  }, [onBack]);

  // If we have auth token and meeting ID, render the meeting
  if (authToken && meetingId) {
    return (
      <MeetingProvider
        config={{
          meetingId,
          micEnabled: true,
          webcamEnabled: true,
          name: "Camera Tester",
        }}
        token={authToken}
      >
        <MeetingConsumer>
          {() => (
            <MeetingView 
              meetingId={meetingId} 
              onMeetingLeave={onMeetingLeave} 
            />
          )}
        </MeetingConsumer>
      </MeetingProvider>
    );
  }

  // Otherwise show the join screen
  return <JoinScreen getMeetingAndToken={getMeetingAndToken} />;
};

export default CameraTestPage;
